// src/components/PlanDetailsModal.jsx
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

const PlanDetailsModal = ({ plan, onClose }) => {
  const { t } = useTranslation();

  if (!plan) return null;

  const isUnlimited = (value) => value === 0 || value === "0";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-2xl font-bold">{plan.name}</h2>
          <p className="text-blue-100 mt-1">{plan.description}</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Price */}
          <div className="text-center">
            <div className="flex items-baseline justify-center">
              <span className="text-5xl font-extrabold text-blue-600">
                ${plan.price}
              </span>
              <span className="ml-2 text-lg text-gray-600">
                /{plan.billing_interval === "monthly" ? t("plan_details.month") : t("plan_details.year")}
              </span>
            </div>
            {plan.trial_days > 0 && (
              <p className="text-sm text-green-600 mt-2">
                {t("plan_details.free_trial", { days: plan.trial_days })}
              </p>
            )}
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {isUnlimited(plan.max_lawyers) ? t("plan_details.unlimited") : plan.max_lawyers}
              </div>
              <div className="text-sm text-gray-600">{t("plan_details.lawyers")}</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {isUnlimited(plan.max_forms_per_month)
                  ? t("plan_details.unlimited")
                  : plan.max_forms_per_month}
              </div>
              <div className="text-sm text-gray-600">{t("plan_details.forms_per_month")}</div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">
              {t("plan_details.features_included")}
            </h3>
            <ul className="space-y-2">
              {plan.features.map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-start text-sm text-gray-700"
                >
                  <svg
                    className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <button
            onClick={() => {
              window.location.href = `/checkout/${plan.id}`;
            }}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition shadow-lg"
          >
            {t("plan_details.choose_plan")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PlanDetailsModal;
